/**
 * Class implementing search functionality for tables.
 */
export class Searchable {
    /**
     * Searchable class constructor.
     */
    constructor() {
        this.tables = [];
        this.searchInputs = new Map();
        document.addEventListener('DOMContentLoaded', () => {
            this.init();
        });
    }

    /**
     * Initializes the search functionality for all tables with the matching attribute.
     */
    init() {
        this.tables = document.querySelectorAll('table[data-dcms-searchable="simple"]');
        this.tables.forEach(table => this._setupSearchForTable(table));
    }

    /**
     * Configures search for a single table.
     * @param {HTMLTableElement} table - Table to configure.
     * @private
     */
    _setupSearchForTable(table) {
        const thead = table.querySelector('thead');
        const firstHeaderCell = thead.querySelector('tr th:first-child');
        const searchRow = document.createElement('tr');

        const searchCell = document.createElement('th');
        searchCell.className = 'px-4 py-2.5 border-b border-gray-200 dark:border-neutral-700';
        searchCell.colSpan = thead.querySelectorAll('th').length;

        const searchInput = document.createElement('input');
        searchInput.type = 'text';
        searchInput.placeholder = __i18n("searchPlaceholder");
        searchInput.className = 'w-full max-w-sm h-8 px-3 text-sm border border-gray-300 rounded-md bg-white outline-none focus:border-indigo-500 dark:bg-neutral-800 dark:text-white dark:border-neutral-600';

        const clearButton = document.createElement('button');
        clearButton.innerHTML = '&times;';
        clearButton.className = 'ml-2 px-2 py-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 text-lg leading-none';
        clearButton.title = __i18n("clearSearch");

        const statusSpan = document.createElement('span');
        statusSpan.className = 'ml-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap';

        const searchContainer = document.createElement('div');
        searchContainer.className = 'flex items-center';
        searchContainer.appendChild(searchInput);
        searchContainer.appendChild(clearButton);
        searchContainer.appendChild(statusSpan);

        searchCell.appendChild(searchContainer);
        searchRow.appendChild(searchCell);
        thead.insertBefore(searchRow, thead.firstChild);

        this.searchInputs.set(table, { input: searchInput, status: statusSpan });

        searchInput.addEventListener('input', () => this._performSearch(table));
        clearButton.addEventListener('click', () => this._clearSearch(table));

        this._updateStatus(table);
    }

    /**
     * Performs the search in the table.
     * @param {HTMLTableElement} table - Table in which the search is performed.
     * @private
     */
    _performSearch(table) {
        const searchInput = this.searchInputs.get(table).input;
        const searchTerm = searchInput.value.toLowerCase();
        let visibleRows = 0;

        table.querySelectorAll('tbody tr').forEach(row => {
            const text = row.textContent.toLowerCase();
            const isVisible = text.includes(searchTerm);
            row.style.display = isVisible ? '' : 'none';
            if (isVisible) visibleRows++;
        });

        this._updateStatus(table, visibleRows);
        this._triggerAfterSearch(table, visibleRows);
    }

    /**
     * Clears the search and restores all rows.
     * @param {HTMLTableElement} table - Table in which the search is cleared.
     * @private
     */
    _clearSearch(table) {
        const searchInput = this.searchInputs.get(table).input;
        searchInput.value = '';
        let visibleRows = 0;
        table.querySelectorAll('tbody tr').forEach(row => {
            row.style.display = '';
            visibleRows++;
        });
        this._updateStatus(table, visibleRows);
        this._triggerAfterSearch(table, visibleRows);
    }

    /**
     * Updates the search status.
     * @param {HTMLTableElement} table - Table for which the status is updated.
     * @param {number} [visibleRows] - Number of visible rows after the search.
     * @private
     */
    _updateStatus(table, visibleRows) {
        const statusSpan = this.searchInputs.get(table).status;
        const totalRows = table.querySelectorAll('tbody tr').length;
        visibleRows = visibleRows !== undefined ? visibleRows : totalRows;
        statusSpan.textContent = __i18n("showingResults").replace('%d', visibleRows).replace('%d', totalRows);
    }

    /**
     * Calls the afterSearch callback, if defined.
     * @param {HTMLTableElement} table - Table for which we call the callback.
     * @param {number} visibleRows - Number of visible rows after the search.
     * @private
     */
    _triggerAfterSearch(table, visibleRows) {
        const afterSearchCallback = table.dataset.dcmsSearchableAftersearch;
        if (afterSearchCallback && typeof window[afterSearchCallback] === 'function') {
            window[afterSearchCallback](table, visibleRows);
        }
    }
}